// App.tsx
import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import CookieConsentBanner from "./components/CookieConsentBanner";
import ProtectedLayout from "./components/ProtectedLayout";
import SocketManager from "./components/SocketManager";
import TokenRefreshIndicator from "./components/TokenRefreshIndicator";
import NotificationCenter from "./components/NotificationCenter";
import FeatureFlagsBanner from "./components/FeatureFlagsBanner";
import SiteBanner from "./components/SiteBanner";
import DocumentSkeleton from "./components/DocumentSkeleton";

// Бывший App.css, разрезанный на части в исходном порядке: стили входа,
// экрана загрузки и страницы отчёта нужны и сборке «только проверки»
// (src/editions/checks).
import "./styles/app/01-base.css";
import "./styles/auth.css";
import "./styles/app/02-document.css";
import "./styles/loading.css";
import "./styles/app/03-pages.css";
import "./styles/report-base.css";
import "./styles/app/04-pages.css";
import "./styles/report-viewer.css";
import "./styles/app/05-pages.css";

// ленивые страницы (по желанию можно добавить webpackChunkName / Prefetch)
const Auth = lazy(() => import("./components/Auth"));
const LocalRegisterPage = lazy(() => import("./components/LocalRegisterPage"));
const CheckEmailPage = lazy(() => import("./components/CheckEmailPage"));
const ForgotPasswordPage = lazy(() => import("./components/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./components/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./components/VerifyEmailPage"));
const YandexCallback = lazy(() => import("./components/YandexCallback"));
const AuthError = lazy(() => import("./components/AuthError"));
const LandingPage = lazy(() => import("./components/LandingPage"));
const DocumentApp = lazy(() => import("./components/DocumentApp"));
const NeuronetsPage = lazy(() => import("./components/NeuronetsPage"));
const WritingPage = lazy(() => import("./components/WritingPage"));
const AdminPanel = lazy(() => import("./components/AdminPanel"));
const Reports = lazy(() => import("./components/Reports"));
const ReportPage = lazy(() => import("./components/ReportViewerPage"));
const UserProfilePage = lazy(() => import("./components/UserProfilePage"));
const NewsPage = lazy(() => import("./components/NewsPage"));
const AutowritePage = lazy(() => import("./components/AutowritePage"));
const ChatPage = lazy(() => import("./components/ChatPage"));
const DashboardPage = lazy(() => import("./components/DashboardPage"));
const SupportChatPage = lazy(() => import("./components/SupportChat/SupportChatPage"));
const PrivacyPolicyPage = lazy(() => import("./components/PrivacyPolicyPage"));
const OfferPage = lazy(() => import("./components/OfferPage"));
const RewriterGuidePage = lazy(() => import("./components/RewriterGuidePage"));

const AuthRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/", search }} replace />;
};

/**
 * Fallback на время подгрузки кода страницы. Чанк обычно приходит за десятки
 * миллисекунд, и прежний тёмный оверлей «Загрузка страницы…» успевал только
 * мигнуть (на входе — на весь экран, в кабинете — поверх светлой страницы).
 * Теперь это нейтральная заглушка без собственного фона; спиннер проявляется,
 * только если загрузка затянулась (> 400 мс).
 */
const PageLoader: React.FC = () => (
  <div className="route-fallback route-fallback--page" role="status" aria-label="Загрузка страницы">
    <span className="route-fallback__spinner" aria-hidden="true" />
  </div>
);

// Fallback для страниц с сайдбаром (защищенные страницы) — только область контента.
const ProtectedPageLoader: React.FC = () => (
  <div className="route-fallback" role="status" aria-label="Загрузка страницы">
    <span className="route-fallback__spinner" aria-hidden="true" />
  </div>
);

// обёртка для ленивых компонентов
type LazyComponent = React.LazyExoticComponent<React.ComponentType<any>>;

interface LazyPageProps {
  component: LazyComponent;
  /** Использовать contentOnly для страниц с сайдбаром */
  withSidebar?: boolean;
  /** Своя заглушка на время загрузки чанка (например, skeleton документа). */
  fallback?: React.ReactNode;
}

const LazyPage: React.FC<LazyPageProps> = ({ component: Component, withSidebar = false, fallback }) => (
  <Suspense fallback={fallback ?? (withSidebar ? <ProtectedPageLoader /> : <PageLoader />)}>
    <Component />
  </Suspense>
);

// обёртка для защищённых страниц
const ProtectedPage: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ProtectedRoute>
      <SocketManager />
      <TokenRefreshIndicator />
    <ProtectedLayout>{children}</ProtectedLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <Router>
      <div className="App">
        <NotificationCenter />
        <FeatureFlagsBanner />
        <SiteBanner />
        <CookieConsentBanner />
        <Routes>
          {/* login также ленивый, но уже обёрнут в Suspense */}
          <Route path="/login" element={<LazyPage component={Auth} />} />
          {/* Реферальные ссылки: /auth?ref=CODE → /?ref=CODE (лендинг, query сохраняется) */}
          <Route path="/auth" element={<AuthRedirect />} />
          <Route path="/auth/register" element={<LazyPage component={LocalRegisterPage} />} />
          <Route path="/auth/check-email" element={<LazyPage component={CheckEmailPage} />} />
          <Route path="/auth/verify-email" element={<LazyPage component={VerifyEmailPage} />} />
          <Route path="/auth/forgot-password" element={<LazyPage component={ForgotPasswordPage} />} />
          <Route path="/auth/reset-password" element={<LazyPage component={ResetPasswordPage} />} />
          
          {/* Yandex OAuth callback и error страницы */}
          <Route path="/auth/yandex/callback" element={<LazyPage component={YandexCallback} />} />
          <Route path="/auth/error" element={<LazyPage component={AuthError} />} />

          {/* Демо-страницы: доступны без авторизации, но действия требуют логина */}
          <Route path="/" element={<LazyPage component={LandingPage} />} />

          {/* Главный экран ЛК — требуется авторизация */}
          <Route
            path="/dashboard"
            element={
              <ProtectedPage>
                <LazyPage component={DashboardPage} withSidebar />
              </ProtectedPage>
            }
          />

          {/* Политика конфиденциальности (ФЗ-152) */}
          <Route path="/privacy" element={<LazyPage component={PrivacyPolicyPage} />} />
          {/* Публичная оферта */}
          <Route path="/offer" element={<LazyPage component={OfferPage} />} />

          <Route
            path="/rewrite-guide"
            element={
              <ProtectedPage>
                <LazyPage component={RewriterGuidePage} withSidebar />
              </ProtectedPage>
            }
          />
          <Route
            path="/rewrite-guide/:scenario"
            element={
              <ProtectedPage>
                <LazyPage component={RewriterGuidePage} withSidebar />
              </ProtectedPage>
            }
          />

          <Route
            path="/app"
            element={
              <ProtectedPage>
                <div className="main-content-container">
                  <LazyPage component={DocumentApp} withSidebar fallback={<DocumentSkeleton withSwitcher />} />
                </div>
              </ProtectedPage>
            }
          />
          <Route
            path="/app/:slotId"
            element={
              <ProtectedPage>
                <div className="main-content-container">
                  <LazyPage component={DocumentApp} withSidebar fallback={<DocumentSkeleton withSwitcher />} />
                </div>
              </ProtectedPage>
            }
          />

          <Route
            path="/autowrite"
            element={
              <ProtectedPage>
                <LazyPage component={AutowritePage} withSidebar />
              </ProtectedPage>
            }
          />

          <Route
            path="/chat"
            element={
              <ProtectedPage>
                <LazyPage component={ChatPage} withSidebar />
              </ProtectedPage>
            }
          />

          <Route
            path="/support"
            element={
              <ProtectedPage>
                <LazyPage component={SupportChatPage} withSidebar />
              </ProtectedPage>
            }
          />

          <Route
            path="/reports"
            element={
              <ProtectedPage>
                <LazyPage component={Reports} withSidebar />
              </ProtectedPage>
            }
          />
          <Route
            path="/reports/:reportId"
            element={<LazyPage component={ReportPage} />}
          />

          <Route
            path="/profile"
            element={
              <ProtectedPage>
                <LazyPage component={UserProfilePage} withSidebar />
              </ProtectedPage>
            }
          />
          <Route
            path="/balance/top-up"
            element={<Navigate to="/profile" replace />}
          />

          {/* <Route
            path="/neuronets"
            element={
              <ProtectedPage>
                <LazyPage component={NeuronetsPage} withSidebar />
              </ProtectedPage>
            }
          />

          <Route
            path="/writing"
            element={
              <ProtectedPage>
                <LazyPage component={WritingPage} withSidebar />
              </ProtectedPage>
            }
          /> */}

          <Route
            path="/admin"
            element={
              <ProtectedPage>
                <LazyPage component={AdminPanel} withSidebar />
              </ProtectedPage>
            }
          />

          {/* Страница новостей - требуется авторизация */}
          <Route
            path="/news"
            element={
              <ProtectedPage>
                <LazyPage component={NewsPage} withSidebar />
              </ProtectedPage>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
